<template>
  <base-slide-over ref="slideOver" title="New Activity Type" @hiding="reset()" size="xl">
    <base-form ref="form" v-model="isFormValid" class="tw-py-2.5 tw-px-4 sm:tw-py-4 sm:tw-px-6">
      <base-text-field label="Label" type="text" placeholder="Enter a label for the activity type" v-model="todoItemType.label" @keyup.enter="save" required />
      <base-text-field label="Group Name" type="text" placeholder="Enter a group name for the activity type" v-model="todoItemType.typeGroup" @keyup.enter="save" class="tw-pb-5" />
      <base-textarea label="Description" placeholder="Enter a description" v-model="todoItemType.description" @keyup.enter="save" required />
      <div class="tw-mt-3">
        <base-advanced-select
          label="Medications"
          placeholder="Select one or more Medications"
          :options="groupedAndSortedMedications"
          :value="selectedMedications"
          @input="onMedicationsChange"
          label-key="label"
          value-key="value"
          multiple
          class="tw-flex-grow"
        />
      </div>
      <div class="tw-mt-4">
        <div v-if="isLoadingRxItems">
          <span>Loading Rx Items...</span>
        </div>
        <base-advanced-select
          v-else
          label="Rx Items"
          placeholder="Select one or more Rx Items"
          :options="groupedAndSortedRxItems"
          :value="selectedRxItems"
          @input="onRxItemsChange"
          label-key="label"
          value-key="value"
          multiple
          class="tw-flex-grow tw-mt-4"
        />
      </div>
      <div class="tw-mt-3">
        <base-checkbox label="Allow assign" v-model="todoItemType.isAssignable" />
      </div>
      <!-- Medication Refills Section -->
      <div class="tw-mt-4">
        <div class="tw-flex tw-justify-between tw-items-center tw-mb-2">
          <label class="tw-block tw-text-sm tw-font-medium tw-text-gray-700">Medication Refills</label>
          <base-button type="text" size="sm" @click="clearMedicationRefills" v-if="medicationRefills.some((m) => m.medicationId || m.refills)">
            Clear All
          </base-button>
        </div>
        <div v-for="(item, index) in medicationRefills" :key="index" class="tw-flex tw-gap-3 tw-mb-3 tw-items-start">
          <base-advanced-select
            placeholder="Select Medication"
            :options="getAvailableMedications(index)"
            :value="item.medicationId"
            @input="updateMedicationId(index, $event)"
            label-key="label"
            value-key="value"
            class="tw-flex-1"
          />
          <base-text-field type="number" placeholder="Refills" v-model.number="item.refills" class="tw-w-32" />
          <div class="tw-mt-1">
            <base-button type="text" size="sm" @click="removeMedicationRefill(index)">
              Clear
            </base-button>
          </div>
        </div>
        <div class="tw-mt-2">
          <base-button type="text" size="sm" @click="addMedicationRefill"> Add Medication </base-button>
        </div>
      </div>
      <inline-error class="tw-mt-3" id="add-business-modal-error" :title="errorTitle" :message="errorMessage" :error="error" />
    </base-form>
    <template #fixed-bottom>
      <div class="tw-py-2 tw-px-3 tw-flex tw-justify-end">
        <base-button @click="hide" type="flat" class="tw-w-auto tw-mr-3">
          Cancel
        </base-button>
        <base-button @click="save" :loading="isLoading" class="tw-w-auto" :disabled="!isFormValid">
          Create
        </base-button>
      </div>
    </template>
  </base-slide-over>
</template>

<script>
import InlineError from "@/components/InlineError";
import orderBy from "lodash/orderBy";

export default {
  name: "NewTodoItemType",
  components: {
    InlineError,
  },
  props: {
    businessId: String,
    allRxItems: {
      type: Array,
      default: () => [],
    },
    allMedications: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      isLoading: false,
      isFormValid: true,
      isLoadingRxItems: false,
      todoItemType: {
        label: null,
        typeGroup: null,
        description: null,
        target: "member",
        isAssignable: false,
      },
      selectedRxItems: [],
      selectedMedications: [],
      medicationRefills: [{ medicationId: null, refills: null }],
      errorTitle: "",
      errorMessage: "",
      error: null,
    };
  },
  computed: {
    groupedAndSortedRxItems() {
      if (!Array.isArray(this.allRxItems) || this.allRxItems.length === 0) {
        return [];
      }

      const suppliesItems = this.allRxItems.filter((item) => item.productGroup === "Supplies");

      const mappedItems = suppliesItems.map((item) => ({
        ...item,
        label: typeof item.name === "string" && item.name.trim() !== "" ? item.name : item.label && item.label.trim() !== "" ? item.label : "Unnamed Rx Item",
        value: item.businessItemCategoryId,
        group: item.productGroup || null,
      }));

      const options = [];
      const groups = [...new Set(mappedItems.map((item) => item.group))].filter(Boolean);

      groups.forEach((group) => {
        options.push({ label: group, value: null, isHeader: true });
        const itemsInGroup = mappedItems.filter((i) => i.group === group).map((i) => ({ label: i.label, value: i.value }));
        options.push(...orderBy(itemsInGroup, ["label"]));
      });

      const noGroupItems = mappedItems.filter((item) => !item.group);
      if (noGroupItems.length > 0) {
        options.push({ label: "Other Rx Items", value: null, isHeader: true });
        const mappedNoGroup = noGroupItems.map((i) => ({ label: i.label, value: i.value }));
        options.push(...orderBy(mappedNoGroup, ["label"]));
      }
      return options;
    },
    groupedAndSortedMedications() {
      if (!Array.isArray(this.allMedications) || this.allMedications.length === 0) {
        return [];
      }
      const mappedItems = this.allMedications.map((item) => ({
        ...item,
        label: typeof item.name === "string" && item.name.trim() !== "" ? item.name : item.label && item.label.trim() !== "" ? item.label : "Unnamed Medications",
        value: item.businessItemCategoryId,
        group: item.productGroup || null,
      }));

      const options = [];
      const groups = [...new Set(mappedItems.map((item) => item.group))].filter(Boolean);

      // Sort groups alphabetically (ascending)
      const sortedGroups = orderBy(groups, [(group) => group.toLowerCase()], ["asc"]);

      sortedGroups.forEach((group) => {
        options.push({ label: group, value: null, isHeader: true });
        const itemsInGroup = mappedItems.filter((i) => i.group === group).map((i) => ({ label: i.label, value: i.value }));
        options.push(...orderBy(itemsInGroup, [(item) => (isNaN(item.label) ? item.label.toLowerCase() : parseFloat(item.label))], ["asc"]));
      });

      const noGroupItems = mappedItems.filter((item) => !item.group);
      if (noGroupItems.length > 0) {
        options.push({ label: "Other Medications", value: null, isHeader: true });
        const mappedNoGroup = noGroupItems.map((i) => ({ label: i.label, value: i.value }));
        options.push(...orderBy(mappedNoGroup, [(item) => (isNaN(item.label) ? item.label.toLowerCase() : parseFloat(item.label))], ["asc"]));
      }

      return options;
    },
  },
  methods: {
    reset() {
      this.todoItemType = {
        label: null,
        typeGroup: null,
        description: null,
        target: "member",
        isAssignable: false,
      };
      this.selectedRxItems = [];
      this.selectedMedications = [];
      this.medicationRefills = [{ medicationId: null, refills: null }];
      this.errorTitle = "";
      this.errorMessage = "";
      this.error = null;
      if (this.$refs.form && typeof this.$refs.form.reset === "function") this.$refs.form.reset();
    },
    show() {
      this.$refs.slideOver.show();
    },
    hide() {
      this.$refs.slideOver.hide();
      this.reset();
    },
    save() {
      if (!this.$refs.form.validate()) return;
      this.isLoading = true;
      const businessItemCategoryIds = this.selectedRxItems
        .map((item) => {
          if (typeof item === "string" || typeof item === "number") {
            return item;
          } else if (item && item.value) {
            return item.value;
          }
          return null;
        })
        .filter((id) => id !== null);

      const medicationIds = (this.selectedMedications || [])
        .map((item) => (typeof item === "string" || typeof item === "number" ? item : item && item.value ? item.value : null))
        .filter((id) => id !== null && id !== undefined);

      const medicationRefills = this.medicationRefills
        .filter((item) => item.medicationId)
        .map((item) => ({
          businessitemcategoryId: typeof item.medicationId === "object" ? item.medicationId.value : item.medicationId,
          refill: item.refills,
        }));

      this.$axios
        .post(`/businesses/${this.businessId}/todo/types`, {
          label: this.todoItemType.label,
          typeGroup: this.todoItemType.typeGroup,
          description: this.todoItemType.description,
          target: this.todoItemType.target,
          isAssignable: this.todoItemType.isAssignable,
          businessItemCategoryIds,
          medicationIds,
          medicationRefills,
        })
        .then(() => {
          this.$emit("typesUpdated", {});
          this.EventBus.$emit("GLOBAL_NOTIFY", {
            title: "Activity Type Saved",
            message: `Your activity type was saved.`,
          });
          this.hide();
        })
        .catch((err) => {
          this.errorTitle = `Error creating activity type`;
          this.errorMessage = "Sorry, we had trouble creating this activity type. The technicians have been notified. Please try again.";
          this.error = err;
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    onRxItemsChange(selected) {
      this.selectedRxItems = Array.isArray(selected) ? selected : [];
    },
    onMedicationsChange(selected) {
      this.selectedMedications = Array.isArray(selected) ? selected : [];
    },
    addMedicationRefill() {
      this.medicationRefills.push({ medicationId: null, refills: null });
    },
    removeMedicationRefill(index) {
      if (this.medicationRefills.length > 1) {
        this.medicationRefills.splice(index, 1);
      } else {
        this.medicationRefills = [{ medicationId: null, refills: null }];
      }
    },
    clearMedicationRefills() {
      this.medicationRefills = [{ medicationId: null, refills: null }];
    },
    updateMedicationId(index, event) {
      if (this.medicationRefills[index]) {
        this.medicationRefills[index].medicationId = event;
      }
    },
    getAvailableMedications(currentIndex) {
      const selectedIds = this.medicationRefills
        .filter((m, i) => i !== currentIndex && m.medicationId)
        .map((m) => String(typeof m.medicationId === "object" ? m.medicationId.value : m.medicationId));

      return this.groupedAndSortedMedications
        .filter((option) => {
          if (option.isHeader) return true;
          return !selectedIds.includes(String(option.value));
        })
        .filter((option, index, self) => {
          if (option.isHeader) {
            const nextItem = self[index + 1];
            return nextItem && !nextItem.isHeader;
          }
          return true;
        });
    },
  },
};
</script>
